import React from 'react'
import {
  Target,
  BarChart3,
  Users,
  DollarSign,
  CheckCircle,
  Award,
  AlertTriangle,
  TrendingUp
} from 'lucide-react'

// Modern Badge component
const Badge = ({ children, className }: { children: React.ReactNode, className?: string }) => (
  <span className={`px-3 py-1 rounded-monumenta text-xs font-display font-medium transition-all duration-200 ${className}`}>
    {children}
  </span>
)

// Modern Progress component
const Progress = ({ value, className }: { value: number, className?: string }) => (
  <div className={`w-full bg-brand-dark-lighter rounded-monumenta h-2 shadow-inner ${className}`}>
    <div 
      className="h-2 rounded-monumenta bg-gradient-to-r from-brand-primary to-brand-accent-600 shadow-monumenta transition-all duration-500" 
      style={{ width: `${value}%` }}
    ></div>
  </div>
)

export const ObjetivosEstrategicosTab = () => (
    <div className="space-y-8 animate-fade-in-up">
      {/* O que esperamos de você? */}
      <div className="card">
        <div className="mb-8">
          <h3 className="font-display text-2xl font-bold text-white flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            <div className="p-2 rounded-monumenta bg-brand-primary/10">
              <Target className="w-6 h-6 text-brand-primary" />
            </div>
            O que esperamos de você?
          </h3>
        </div>
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Objetivo 1 */}
            <div className="card-interactive group p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-monumenta bg-brand-blue/10 group-hover:bg-brand-blue/20 transition-colors duration-200">
                  <BarChart3 className="w-6 h-6 text-brand-blue group-hover:scale-110 transition-transform duration-200" />
                </div>
                <h4 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Planejamento Estratégico</h4>
              </div>
              <p className="text-sm text-brand-gray-light font-body mb-4 leading-relaxed">
                Definir atuação do planejamento promocional e digital com planos de ação estruturados
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-brand-gray font-body">Meta Anual</span>
                <Badge className="bg-brand-blue/20 text-brand-blue border border-brand-blue/30">
                  95%
                </Badge>
              </div>
            </div>

            {/* Objetivo 2 */}
            <div className="card-interactive group p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-monumenta bg-success/10 group-hover:bg-success/20 transition-colors duration-200">
                  <Users className="w-6 h-6 text-success group-hover:scale-110 transition-transform duration-200" />
                </div>
                <h4 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Gestão de Equipe</h4>
              </div>
              <p className="text-sm text-brand-gray-light font-body mb-4 leading-relaxed">
                Dar feedbacks Claros, Alinhar expectativas e garantir performance
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-brand-gray font-body">Meta Anual</span>
                <Badge className="bg-success/20 text-success border border-success/30">
                  90%
                </Badge>
              </div>
            </div>

            {/* Objetivo 3 */}
            <div className="card-interactive group p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-monumenta bg-brand-accent-600/10 group-hover:bg-brand-accent-600/20 transition-colors duration-200">
                  <DollarSign className="w-6 h-6 text-brand-accent-600 group-hover:scale-110 transition-transform duration-200" />
                </div>
                <h4 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Crescimento de Receita</h4>
              </div>
              <p className="text-sm text-brand-gray-light font-body mb-4 leading-relaxed">
                Gerar jobs proativos que comprovem aumento de receita para a agência
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-brand-gray font-body">Meta Anual</span>
                <Badge className="bg-brand-accent-600/20 text-brand-accent-600 border border-brand-accent-600/30">
                  20%
                </Badge>
              </div>
            </div>
          </div>

          {/* Lista detalhada de expectativas */}
          <div className="space-y-6">
            <h4 className="font-display text-lg text-white font-semibold">Expectativas Principais</h4>
            <div className="space-y-4">
              <div className="card-interactive group flex items-start gap-4 p-4">
                <CheckCircle className="w-6 h-6 text-brand-blue mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <div>
                  <p className="text-white font-display font-medium group-hover:text-brand-primary transition-colors duration-200">Definição de Estratégias</p>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">Elaborar planos de ação para frentes promocionais e digitais com objetivos claros</p>
                </div>
              </div>
              <div className="card-interactive group flex items-start gap-4 p-4">
                <CheckCircle className="w-6 h-6 text-success mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <div>
                  <p className="text-white font-display font-medium group-hover:text-brand-primary transition-colors duration-200">Coordenação de BI e Planejamento</p>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">Estabelecer metas derivadas da direção e manter sistemas atualizados</p>
                </div>
              </div>
              <div className="card-interactive group flex items-start gap-4 p-4">
                <CheckCircle className="w-6 h-6 text-brand-accent-600 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <div>
                  <p className="text-white font-display font-medium group-hover:text-brand-primary transition-colors duration-200">Estratégias por Cliente</p>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">Definir áreas de atuação e comunicar estratégias claras à equipe</p>
                </div>
              </div>
              <div className="card-interactive group flex items-start gap-4 p-4">
                <CheckCircle className="w-6 h-6 text-warning mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <div>
                  <p className="text-white font-display font-medium group-hover:text-brand-primary transition-colors duration-200">Melhoria do Clima Organizacional</p>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">Contribuir ativamente para melhoria do ambiente de trabalho da agência</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Como Gestor */}
      <div className="card">
        <div className="mb-8">
          <h3 className="font-display text-2xl font-bold text-white flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            <div className="p-2 rounded-monumenta bg-brand-primary/10">
              <Award className="w-6 h-6 text-brand-primary" />
            </div>
            Como Gestor
          </h3>
        </div>
        <div className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Gestão de Equipe - Competências */}
            <div className="space-y-6">
              <h4 className="font-display text-lg text-white font-semibold">Gestão de Equipe</h4>
              <div className="space-y-4">
                <div className="card-interactive group flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-brand-blue rounded-full animate-pulse"></div>
                    <span className="text-white font-body group-hover:text-brand-primary transition-colors duration-200">Compatibilidade de Perfis</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={88} className="w-20" />
                    <span className="text-sm text-brand-gray-light font-display font-semibold">88%</span>
                  </div>
                </div>
                <div className="card-interactive group flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
                    <span className="text-white font-body group-hover:text-brand-primary transition-colors duration-200">Acompanhamento de Pauta</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={92} className="w-20" />
                    <span className="text-sm text-brand-gray-light font-display font-semibold">92%</span>
                  </div>
                </div>
                <div className="card-interactive group flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-brand-accent-600 rounded-full animate-pulse"></div>
                    <span className="text-white font-body group-hover:text-brand-primary transition-colors duration-200">Capacitação da Equipe</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={85} className="w-20" />
                    <span className="text-sm text-brand-gray-light font-display font-semibold">85%</span>
                  </div>
                </div>
                <div className="card-interactive group flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-warning rounded-full animate-pulse"></div>
                    <span className="text-white font-body group-hover:text-brand-primary transition-colors duration-200">Feedbacks Documentados</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={90} className="w-20" />
                    <span className="text-sm text-brand-gray-light font-display font-semibold">90%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Responsabilidades Estratégicas */}
            <div className="space-y-6">
              <h4 className="font-display text-lg text-white font-semibold">Responsabilidades Estratégicas</h4>
              <div className="space-y-4">
                <div className="card-interactive group p-5">
                  <div className="flex items-center gap-4 mb-3">
                    <BarChart3 className="w-5 h-5 text-brand-blue group-hover:scale-110 transition-transform duration-200" />
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Coordenação de BI</h5>
                  </div>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">
                    Estabelecer metas claras para coordenação e manter Wrike atualizado
                  </p>
                </div>
                <div className="card-interactive group p-5">
                  <div className="flex items-center gap-4 mb-3">
                    <Target className="w-5 h-5 text-success group-hover:scale-110 transition-transform duration-200" />
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Estratégias por Cliente</h5>
                  </div>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">
                    Definir áreas de atuação e comunicar estratégias à equipe
                  </p>
                </div>
                <div className="card-interactive group p-5">
                  <div className="flex items-center gap-4 mb-3">
                    <TrendingUp className="w-5 h-5 text-brand-accent-600 group-hover:scale-110 transition-transform duration-200" />
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Performance da Equipe</h5>
                  </div>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">
                    Acompanhar desempenho técnico e comportamental dos profissionais
                  </p>
                </div>
                <div className="card-interactive group p-5 border-brand-red/30 bg-brand-red/5">
                  <div className="flex items-center gap-4 mb-3">
                    <AlertTriangle className="w-5 h-5 text-brand-red group-hover:scale-110 transition-transform duration-200" />
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Decisões de Desligamento</h5>
                  </div>
                  <p className="text-sm text-brand-gray-light font-body leading-relaxed">
                    Tomar decisões rápidas sobre profissionais com desempenho insatisfatório
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Para a equipe */}
      <div className="card">
        <div className="mb-8">
          <h3 className="font-display text-2xl font-bold text-white flex items-center gap-3 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            <div className="p-2 rounded-monumenta bg-brand-primary/10">
              <Users className="w-6 h-6 text-brand-primary" />
            </div>
            Para a equipe
          </h3>
        </div>
        <div className="space-y-8">
          {/* Objetivos Coletivos */}
          <div>
            <h4 className="font-display text-lg text-white font-semibold mb-6">Objetivos Coletivos 2024</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="card-interactive group p-6 bg-gradient-to-r from-brand-blue/10 to-brand-blue/5 border border-brand-blue/30">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Campanhas Entregues</h5>
                    <Badge className="bg-brand-blue text-white">Meta</Badge>
                  </div>
                  <p className="text-3xl font-bold font-display text-white mb-3">95%</p>
                  <p className="text-sm text-brand-gray-light font-body">Campanhas no prazo e no brief</p>
                  <Progress value={89} className="mt-4" />
                  <p className="text-xs text-brand-gray font-body mt-2">Atual: 89%</p>
                </div>
                <div className="card-interactive group p-6 bg-gradient-to-r from-success/10 to-success/5 border border-success/30">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Satisfação do Cliente</h5>
                    <Badge className="bg-success text-white">Meta</Badge>
                  </div>
                  <p className="text-3xl font-bold font-display text-white mb-3">4.5</p>
                  <p className="text-sm text-brand-gray-light font-body">Avaliação NPS clientes</p>
                  <Progress value={88} className="mt-4" />
                  <p className="text-xs text-brand-gray font-body mt-2">Atual: 4.4</p>
                </div>
              </div>
              <div className="space-y-6">
                <div className="card-interactive group p-6 bg-gradient-to-r from-brand-accent-600/10 to-brand-accent-600/5 border border-brand-accent-600/30">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Jobs Proativos</h5>
                    <Badge className="bg-brand-accent-600 text-white">Meta</Badge>
                  </div>
                  <p className="text-3xl font-bold font-display text-white mb-3">20%</p>
                  <p className="text-sm text-brand-gray-light font-body">Aumento de receita comprovado</p>
                  <Progress value={15} className="mt-4" />
                  <p className="text-xs text-brand-gray font-body mt-2">Atual: 15%</p>
                </div>
                <div className="card-interactive group p-6 bg-gradient-to-r from-warning/10 to-warning/5 border border-warning/30">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-white font-display font-semibold group-hover:text-brand-primary transition-colors duration-200">Wrike Atualizado</h5>
                    <Badge className="bg-warning text-white">Meta</Badge>
                  </div>
                  <p className="text-3xl font-bold font-display text-white mb-3">100%</p>
                  <p className="text-sm text-brand-gray-light font-body">Projetos com status atualizado</p>
                  <Progress value={92} className="mt-4" />
                  <p className="text-xs text-brand-gray font-body mt-2">Atual: 92%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Resumo de Performance */}
          <div className="card">
            <h4 className="font-display text-lg text-white font-semibold mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">Resumo Geral da Equipe</h4>
            <div className="dashboard-grid md:grid-cols-4">
              <div className="metric-card group text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 rounded-monumenta bg-success/10 group-hover:bg-success/20 transition-colors duration-200">
                    <CheckCircle className="w-8 h-8 text-success group-hover:scale-110 transition-transform duration-200" />
                  </div>
                </div>
                <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">6</p>
                <p className="text-sm text-brand-gray-light font-body">Profissionais na Equipe</p>
              </div>
              
              <div className="metric-card group text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 rounded-monumenta bg-brand-blue/10 group-hover:bg-brand-blue/20 transition-colors duration-200">
                    <Target className="w-8 h-8 text-brand-blue group-hover:scale-110 transition-transform duration-200" />
                  </div>
                </div>
                <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">91%</p>
                <p className="text-sm text-brand-gray-light font-body">Performance Média</p>
              </div>
              
              <div className="metric-card group text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 rounded-monumenta bg-brand-accent-600/10 group-hover:bg-brand-accent-600/20 transition-colors duration-200">
                    <TrendingUp className="w-8 h-8 text-brand-accent-600 group-hover:scale-110 transition-transform duration-200" />
                  </div>
                </div>
                <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">89%</p>
                <p className="text-sm text-brand-gray-light font-body">Metas no Prazo</p>
              </div>
              
              <div className="metric-card group text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 rounded-monumenta bg-warning/10 group-hover:bg-warning/20 transition-colors duration-200">
                    <Award className="w-8 h-8 text-warning group-hover:scale-110 transition-transform duration-200" />
                  </div>
                </div>
                <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">4.4</p>
                <p className="text-sm text-brand-gray-light font-body">Satisfação Interna</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );